import type { AdminMaintenance } from '@/admin/adminApi';
import type { SensitiveActionKind } from '@/admin/sensitiveActions';

/**
 * Regles de l'ecran de maintenance, sans rendu.
 *
 * Tout ce qui est ici est **pur** : conversion entre le champ de date du
 * navigateur et l'instant que le serveur range, diff entre le brouillon et l'etat
 * du serveur, lecture defensive des reponses. C'est ce qui permet de verrouiller
 * les regles delicates sans monter React Admin.
 *
 * **Le serveur reste seul juge** de la fenetre (fin apres debut, debut non passe,
 * 72 h au plus) : l'ecran n'en rejoue que ce qui se voit sans horloge, pour ne
 * pas faire retaper un mot de passe a quelqu'un dont les deux champs se
 * contredisent.
 */

/** Cles du serveur (`SettingRegistry`). Le nom vient d'elles, jamais d'un libelle. */
export const MAINTENANCE_ENABLED_KEY = 'maintenance.enabled';
export const MAINTENANCE_MESSAGE_KEY = 'maintenance.message';
export const MAINTENANCE_STARTS_AT_KEY = 'maintenance.starts_at';
export const MAINTENANCE_ENDS_AT_KEY = 'maintenance.ends_at';

/** Famille de reglages que designe la confirmation d'une ecriture groupee. */
export const MAINTENANCE_CONFIRMATION = 'maintenance';

/** Plafond du message, en caracteres : le serveur le tient aussi (`SettingRegistry`). */
export const MESSAGE_MAX_LENGTH = 160;

/** Duree maximale d'une fenetre, en heures : decision du CTO, tenue par le serveur. */
export const MAX_WINDOW_HOURS = 72;

// ---------------------------------------------------------------------------
// Brouillon et diff
// ---------------------------------------------------------------------------

/** Ce que l'operateur edite. Les deux dates sont des valeurs de champ `datetime-local`. */
export interface MaintenanceDraft {
  readonly enabled: boolean;
  /** `AAAA-MM-JJTHH:mm` dans le fuseau du navigateur, ou `''`. */
  readonly start: string;
  readonly end: string;
  readonly message: string;
}

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Valeur d'un champ `datetime-local` pour un instant du serveur.
 *
 * L'instant est lu dans le fuseau du **navigateur** : c'est celui dans lequel
 * l'operateur pense, et l'ecran l'ecrit (`timeZoneLabel`).
 */
export const toLocalInput = (iso: string | null): string => {
  if (iso === null || iso === '') {
    return '';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Instant envoye au serveur pour une valeur de champ : ISO 8601 en UTC (`Z`),
 * sans fraction, ou `null` quand le champ est vide ou illisible.
 *
 * Le champ est lu comme une heure **locale** (`new Date('AAAA-MM-JJTHH:mm')`),
 * puis ecrite avec un decalage explicite : le serveur n'a jamais a deviner un
 * fuseau.
 */
export const toInstant = (local: string): string | null => {
  if (local === '') {
    return null;
  }

  const date = new Date(local);

  return Number.isNaN(date.getTime()) ? null : date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

export const draftFromServer = (state: AdminMaintenance): MaintenanceDraft => ({
  enabled: state.enabled,
  start: toLocalInput(state.startsAt),
  end: toLocalInput(state.endsAt),
  message: state.message,
});

/**
 * Valeurs a ecrire : **seulement ce qui a change**.
 *
 * Re-envoyer une fenetre inchangee demanderait un mot de passe pour rien — le
 * serveur juge « programmer » sur la presence d'un debut ou d'une fin non vides.
 * Les deux bornes partent **ensemble** des que l'une change : le serveur les juge
 * en paire, et une borne seule serait refusee ou jugee contre une valeur que
 * l'operateur ne voit pas.
 */
export const changesOf = (draft: MaintenanceDraft, server: AdminMaintenance): Readonly<Record<string, boolean | string>> => {
  const values: Record<string, boolean | string> = {};

  if (draft.enabled !== server.enabled) {
    values[MAINTENANCE_ENABLED_KEY] = draft.enabled;
  }

  // On compare les VALEURS DE CHAMP, pas des instants tronques : une borne que
  // l'API a ecrite avec des secondes, ou posee dans l'heure repetee d'un
  // changement d'heure, ne doit pas faire paraitre le formulaire modifie des
  // son chargement — l'enregistrer decalerait le debut d'une fenetre en cours.
  if (draft.start !== toLocalInput(server.startsAt) || draft.end !== toLocalInput(server.endsAt)) {
    values[MAINTENANCE_STARTS_AT_KEY] = toInstant(draft.start) ?? '';
    values[MAINTENANCE_ENDS_AT_KEY] = toInstant(draft.end) ?? '';
  }

  if (draft.message.trim() !== server.message.trim()) {
    values[MAINTENANCE_MESSAGE_KEY] = draft.message.trim();
  }

  return values;
};

/**
 * Action sensible que le serveur jugera pour ces valeurs.
 *
 * **Miroir de `ApplicationSettingsService::kindFor`** : activer — ou programmer
 * une fenetre, c'est-a-dire ecrire un debut ou une fin non vides — est l'action
 * lourde, qui exige le mot de passe. Desactiver, effacer la fenetre ou n'ecrire
 * que le message est legere : retablir le service ne doit pas etre plus difficile
 * que de le couper.
 */
export const sensitiveKindFor = (values: Readonly<Record<string, boolean | string>>): SensitiveActionKind => {
  const schedules = [MAINTENANCE_STARTS_AT_KEY, MAINTENANCE_ENDS_AT_KEY].some((key) => {
    const value = values[key];

    return typeof value === 'string' && value.trim() !== '';
  });

  return values[MAINTENANCE_ENABLED_KEY] === true || schedules ? 'enable_maintenance' : 'update_setting';
};

// ---------------------------------------------------------------------------
// Validation de la fenetre
// ---------------------------------------------------------------------------

/** Codes d'erreur que le serveur rend pour une fenetre refusee (`MaintenanceWindowViolation`). */
export type WindowViolation =
  | 'window_incomplete'
  | 'window_ends_before_start'
  | 'window_starts_in_past'
  | 'window_too_long';

interface ViolationDisplay {
  /** Champ sous lequel le refus s'affiche. */
  readonly field: 'start' | 'end';
  readonly message: string;
}

/** Une seule table : les refus locaux et ceux du serveur disent la meme phrase. */
export const WINDOW_VIOLATIONS: Readonly<Record<WindowViolation, ViolationDisplay>> = {
  window_incomplete: {
    field: 'end',
    message: 'Renseignez le début et la fin, ou videz les deux champs pour effacer la fenêtre.',
  },
  window_ends_before_start: { field: 'end', message: 'La fin doit être strictement après le début.' },
  window_starts_in_past: { field: 'start', message: 'Le début ne peut pas être dans le passé.' },
  window_too_long: {
    field: 'end',
    message: `Une fenêtre dure ${String(MAX_WINDOW_HOURS)} heures au plus : avancez la fin.`,
  },
};

export const isWindowViolation = (code: string | null): code is WindowViolation =>
  code !== null && Object.prototype.hasOwnProperty.call(WINDOW_VIOLATIONS, code);

/**
 * Ce qui se voit sans horloge : une borne manque, ou la fin ne suit pas le debut.
 * Le debut passe et la duree maximale restent au serveur.
 */
export const localWindowViolation = (draft: MaintenanceDraft): WindowViolation | null => {
  const start = toInstant(draft.start);
  const end = toInstant(draft.end);

  if (start === null && end === null) {
    return null;
  }

  if (start === null || end === null) {
    return 'window_incomplete';
  }

  return new Date(end).getTime() <= new Date(start).getTime() ? 'window_ends_before_start' : null;
};

// ---------------------------------------------------------------------------
// Fuseau
// ---------------------------------------------------------------------------

/**
 * Fuseau du navigateur, ecrit en toutes lettres : « Europe/Paris (UTC+02:00) ».
 *
 * La saisie se fait dans ce fuseau et le serveur range l'instant avec son
 * decalage. L'ecran le dit, sans quoi « 02:00 » se lirait comme l'heure de
 * l'utilisateur final, ou celle du serveur.
 */
export const timeZoneLabel = (at: Date = new Date()): string => {
  // Le decalage est celui de la DATE concernee, pas d'aujourd'hui : une fenetre
  // saisie en octobre pour le 1er novembre est en UTC+01:00, pas +02:00.
  const offset = -at.getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const magnitude = Math.abs(offset);
  const name = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return `${name} (UTC${sign}${pad(Math.floor(magnitude / 60))}:${pad(magnitude % 60)})`;
};

/** Fuseau a ecrire pour une valeur de champ : celui de **cette** date, ou d'aujourd'hui si elle est vide. */
export const timeZoneLabelFor = (local: string): string => {
  const date = local === '' ? new Date() : new Date(local);

  return timeZoneLabel(Number.isNaN(date.getTime()) ? new Date() : date);
};

// ---------------------------------------------------------------------------
// Lecture defensive
// ---------------------------------------------------------------------------

const nullableString = (value: unknown): string | null | undefined =>
  value === null ? null : typeof value === 'string' ? value : undefined;

/**
 * Valide `GET /admin/maintenance`. Une reponse qui n'en est pas une n'est pas
 * affichee : un etat de service invente serait pire que l'erreur.
 */
export const readMaintenance = (body: unknown): AdminMaintenance | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const record = body as Record<string, unknown>;
  const startsAt = nullableString(record.startsAt);
  const endsAt = nullableString(record.endsAt);
  const nextChangeAt = nullableString(record.nextChangeAt);

  if (
    typeof record.enabled !== 'boolean' ||
    typeof record.activeNow !== 'boolean' ||
    typeof record.message !== 'string' ||
    startsAt === undefined ||
    endsAt === undefined ||
    nextChangeAt === undefined
  ) {
    return null;
  }

  return {
    enabled: record.enabled,
    startsAt,
    endsAt,
    message: record.message,
    activeNow: record.activeNow,
    nextChangeAt,
  };
};

/** Derniere ecriture des reglages de maintenance : quand, et par quel compte. */
export interface LastWrite {
  readonly at: string;
  readonly by: string | null;
}

/** Plus recente `updatedAt` d'une liste de reglages, ou `null` : aucun n'a jamais ete ecrit. */
export const lastWriteOf = (items: readonly unknown[]): LastWrite | null => {
  let latest: LastWrite | null = null;

  for (const item of items) {
    if (typeof item !== 'object' || item === null) {
      continue;
    }

    const { key, updatedAt, updatedBy } = item as Record<string, unknown>;

    if (typeof key !== 'string' || !key.startsWith('maintenance.') || typeof updatedAt !== 'string') {
      continue;
    }

    if (latest === null || new Date(updatedAt).getTime() > new Date(latest.at).getTime()) {
      latest = { at: updatedAt, by: typeof updatedBy === 'string' ? updatedBy : null };
    }
  }

  return latest;
};

// ---------------------------------------------------------------------------
// Etat de la fenetre, tel que le serveur le calcule
// ---------------------------------------------------------------------------

export type WindowPhase = 'none' | 'upcoming' | 'running' | 'ended' | 'overridden';

/**
 * Ou en est la fenetre, **sans horloge de navigateur** : tout vient de
 * `activeNow` et `nextChangeAt`, calcules par le serveur.
 *
 * - avant la fenetre : le prochain changement est son debut ;
 * - pendant : c'est sa fin, et le service est suspendu par elle ;
 * - apres : plus de changement, et le service ne l'est pas — « terminee » ;
 * - commutateur allume : la fenetre ne change plus rien (`nextChangeAt` nul),
 *   le serveur ne dit pas si elle est passee, l'ecran ne le devine pas.
 */
export const windowPhaseOf = (state: AdminMaintenance): WindowPhase => {
  if (state.startsAt === null || state.endsAt === null) {
    return 'none';
  }

  if (state.enabled) {
    return 'overridden';
  }

  if (state.nextChangeAt === null) {
    return 'ended';
  }

  return state.activeNow ? 'running' : 'upcoming';
};
